/* eslint-disable unicorn/filename-case */
import { useForm } from '@tanstack/react-form';
import { ArrowLeft } from '@untitled-ui/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../../components/buttons/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/dialog';
import { StepIndicator } from '../../../components/indicators/step-indicator';
import { ToastVariant } from '../../../components/toast';
import { getConfig } from '../../../config';
import { ForbiddenError } from '../../../error';
import { convertError } from '../../../error/convert';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { useBrandById } from '../../../services/storage/brands-queries';
import { tNamespace } from '../../../services/translation';
import { useToast } from '../../../shared/contexts/toast-context';
import { RewriteContentSourceForm } from './-rewrite-content-source-form';
import { RewriteKeywordForm } from './-rewrite-keyword-form';
import { RewriteOptimizationForm } from './-rewrite-optimization-form';

const t = tNamespace('app.blogs.-modal-rewrite');
const tErrors = tNamespace('errors');

type ModalRewriteProps = {
  brandId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  onRewrite: (params: {
    country: string;
    language: string;
    mode: string;
    chosenTitle: string;
    url: string | undefined | null;
    tiptapJsonContent: Record<string, any> | undefined | null;
    extraInformation: string;
    numberOfResults: number;
    primaryPrompt: string;
    primaryKeyword: string;
    targetWordCount: string | number;
  }) => Promise<void>;
};

type Step = 1 | 2 | 3;

function getSteps() {
  return [
    { id: 1 as const, label: t('contentSource') },
    { id: 2 as const, label: t('keyword') },
    { id: 3 as const, label: t('optimization') },
  ];
}

export function ModalRewrite(props: ModalRewriteProps) {
  const { isOpen, onOpenChange, brandId, onRewrite, initialUrl } = props;
  const { data: brand } = useBrandById(brandId);
  const { defaultCountry, defaultLanguage } = getConfig();

  const [isInternalLoading, setIsInternalLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const addToast = useToast();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const checkScrollPosition = useCallback(function () {
    const container = scrollContainerRef.current;
    if (container == null) {
      setShowFooterShadow(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

    setShowFooterShadow(isScrollable && !isAtBottom);
  }, []);

  const scrollContainerCallbackRef = useCallback(
    function (node: HTMLDivElement | null) {
      if (scrollContainerRef.current != null) {
        scrollContainerRef.current.removeEventListener('scroll', checkScrollPosition);
      }
      if (resizeObserverRef.current != null) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      scrollContainerRef.current = node;

      if (node != null) {
        checkScrollPosition();
        node.addEventListener('scroll', checkScrollPosition);
        resizeObserverRef.current = new ResizeObserver(checkScrollPosition);
        resizeObserverRef.current.observe(node);
      }
    },
    [checkScrollPosition]
  );

  const form = useForm<{
    country: string;
    language: string;
    chosenTitle: string;
    // Kept in type for compatibility with step 2/3 form components; always 'text'
    providedContentType: 'url' | 'text';
    url: string;
    tiptapJsonContent: Record<string, any> | null;
    primaryKeyword: string;
    primaryPrompt: string;
    mode: 'light' | 'major';
    contentLength: 'auto' | 'same' | 'custom';
    contentLengthValue: number;
    extraInformation: string;
  }>({
    defaultValues: {
      // Step 1: Content source
      country: brand?.country?.toLowerCase() ?? defaultCountry.toLowerCase(),
      language: brand?.language?.toLowerCase() ?? defaultLanguage.toLowerCase(),
      chosenTitle: '',
      providedContentType: 'text' as 'url' | 'text',
      url: initialUrl ?? '',
      tiptapJsonContent: null as Record<string, any> | null,
      // Step 2: Keyword & prompt
      primaryKeyword: '',
      primaryPrompt: '',
      // Step 3: Optimization
      mode: 'light' as 'light' | 'major',
      contentLength: 'auto' as 'auto' | 'same' | 'custom',
      contentLengthValue: 50,
      extraInformation: '',
    },
    onSubmit: async function (_data) {
      // do not go through if already in there
      if (isInternalLoading) {
        return;
      }

      // Validate contentLengthValue if custom is selected
      if (form.state.values.contentLength === 'custom') {
        await form.validateField('contentLengthValue', 'submit');
        const contentLengthValueErrors = form.getFieldMeta('contentLengthValue')?.errors;
        if (contentLengthValueErrors != null && contentLengthValueErrors.length > 0) {
          return;
        }
      }

      setIsInternalLoading(true);

      return onRewrite({
        country: form.state.values.country,
        language: form.state.values.language,
        mode: form.state.values.mode,
        chosenTitle: form.state.values.chosenTitle,
        url: form.state.values.url.length > 0 ? form.state.values.url : null,
        tiptapJsonContent: form.state.values.tiptapJsonContent,
        extraInformation: form.state.values.extraInformation,
        numberOfResults: 10, // NOTE: this is a fallback value, it's never updated
        primaryPrompt: form.state.values.primaryKeyword,
        primaryKeyword: form.state.values.primaryKeyword,
        targetWordCount:
          form.state.values.contentLength === 'custom'
            ? form.state.values.contentLengthValue
            : form.state.values.contentLength,
      }).finally(function () {
        setIsInternalLoading(false);
      });
    },
  });

  async function handleContinue() {
    if (currentStep > 3) {
      return;
    }

    // Validate fields based on current step
    let isValid = false;

    try {
      switch (currentStep) {
        case 1: {
          // Validate Step 1 fields
          await form.validateField('chosenTitle', 'change');
          await form.validateField('country', 'change');
          await form.validateField('language', 'change');
          await form.validateField('tiptapJsonContent', 'change');

          // Check for errors
          const chosenTitleErrors = form.getFieldMeta('chosenTitle')?.errors;
          const countryErrors = form.getFieldMeta('country')?.errors;
          const languageErrors = form.getFieldMeta('language')?.errors;
          const tiptapErrors = form.getFieldMeta('tiptapJsonContent')?.errors;

          const hasErrors =
            (chosenTitleErrors != null && chosenTitleErrors.length > 0) ||
            (countryErrors != null && countryErrors.length > 0) ||
            (languageErrors != null && languageErrors.length > 0) ||
            (tiptapErrors != null && tiptapErrors.length > 0);

          isValid = !hasErrors;
          break;
        }
        case 2: {
          // Validate Step 2 fields
          await form.validateField('primaryKeyword', 'change');

          const primaryKeywordErrors = form.getFieldMeta('primaryKeyword')?.errors;
          const hasErrors = primaryKeywordErrors != null && primaryKeywordErrors.length > 0;

          isValid = !hasErrors;
          break;
        }
        case 3: {
          // Validate Step 3 fields
          const contentLength = form.getFieldValue('contentLength');

          if (contentLength === 'custom') {
            await form.validateField('contentLengthValue', 'change');
            const contentLengthValueErrors = form.getFieldMeta('contentLengthValue')?.errors;
            const hasErrors =
              contentLengthValueErrors != null && contentLengthValueErrors.length > 0;
            isValid = !hasErrors;
          } else {
            isValid = true;
          }
          break;
        }
        default: {
          isValid = true;
          break;
        }
      }

      // Only proceed if validation passed
      if (isValid) {
        setCurrentStep((currentStep + 1) as Step);
      }
    } catch (err) {
      captureException(
        err,
        {
          file: 'app/blogs/-modal-rewrite.tsx',
          method: 'handleContinue',
          infoMessage: 'validation error',
        },
        trackError
      );
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function renderStepContent() {
    switch (currentStep) {
      case 1:
        return <RewriteContentSourceForm form={form} />;
      case 2:
        return (
          <div className="flex flex-col gap-4">
            <RewriteKeywordForm form={form} brandId={brandId} />
          </div>
        );
      case 3:
        return <RewriteOptimizationForm form={form} />;
      default:
        return null;
    }
  }

  useEffect(
    function () {
      return function () {
        if (scrollContainerRef.current != null) {
          scrollContainerRef.current.removeEventListener('scroll', checkScrollPosition);
        }
        if (resizeObserverRef.current != null) {
          resizeObserverRef.current.disconnect();
        }
      };
    },
    [checkScrollPosition]
  );

  // Reset step when modal closes
  useEffect(
    function () {
      if (!isOpen) {
        setCurrentStep(1);
      }
    },
    [isOpen]
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          hasCloseButton={true}
          fixedHeight
          aria-describedby={undefined}
          className="w-full max-w-[600px] px-0"
        >
          <DialogHeader className="flex flex-col gap-6">
            <div>
              <DialogTitle align="center">{t('modalTitle')}</DialogTitle>
              {/*
              <button
                type="button"
                className="font-body-3-m mt-1 flex w-full items-center justify-center gap-1 text-branding-accent"
                onClick={function () {
                  setIsVideoOpen(true);
                }}
              >
                <PlayCircleIcon />
                {t('seeHowItWorks')}
              </button>
              */}
            </div>

            <div className="flex items-center justify-center">
              {getSteps().map(function (step, index, steps) {
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;
                return (
                  <div key={step.id} className="flex items-center">
                    <StepIndicator
                      step={step.id}
                      isCurrent={isCurrent}
                      isCompleted={isCompleted}
                      label={step.label}
                    />
                    {index < steps.length - 1 && (
                      <div className="mx-1 h-[1px] w-6 border-b border-b-branding-gray" />
                    )}
                  </div>
                );
              })}
            </div>
          </DialogHeader>

          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={function (e) {
              e.preventDefault();
              e.stopPropagation();
              if (currentStep === 3) {
                form.handleSubmit().catch(function (err) {
                  const handledErr = convertError(err);

                  // handle forbiddens (which comes from plan features limitations)
                  // as such, the original message is a specific enum incoming
                  if (handledErr instanceof ForbiddenError) {
                    addToast({
                      variant: ToastVariant.Error,
                      title: tErrors(handledErr.originalMessage),
                      id: Date.now(),
                    });
                    return;
                  }

                  captureException(
                    handledErr,
                    {
                      file: 'app/blogs/-modal-rewrite.tsx',
                      method: 'onSubmit',
                      infoMessage: 'unable to rewrite content',
                    },
                    trackError
                  );
                });
              } else {
                void handleContinue();
              }
            }}
          >
            <DialogBody ref={scrollContainerCallbackRef} className="mt-4 px-4 md:mt-8 md:px-6">
              {renderStepContent()}
            </DialogBody>

            <DialogFooter
              className="relative z-10 mt-0 flex gap-3 bg-white px-6 pt-6 transition-shadow duration-200"
              style={
                showFooterShadow
                  ? {
                      boxShadow:
                        '0 -20px 24px -4px rgba(10, 13, 18, 0.08), 0 -8px 8px -4px rgba(10, 13, 18, 0.03)',
                    }
                  : undefined
              }
            >
              {currentStep === 1 ? (
                <Button size="xl" variant="outline" className="flex-1" onClick={handleCancel}>
                  {t('cancel')}
                </Button>
              ) : (
                <Button
                  size="xl"
                  variant="outline"
                  className={twMerge(currentStep === 3 ? 'flex-full' : 'flex-1')}
                  onClick={handleBack}
                  type="button"
                >
                  <ArrowLeft className="h-5 w-5" />
                  {t('back')}
                </Button>
              )}

              <Button
                size="xl"
                variant="accent"
                className="flex-1"
                type="submit"
                disabled={isInternalLoading}
              >
                {currentStep === 3
                  ? t('rewriteContent', { creditCost: getConfig().creditPerRewrite })
                  : t('continue')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
